import React, { useEffect, useState } from 'react';
import { Tabs, Upload, Button, Table, Form, Input, Select, Space, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import axios from '../utils/http';
import SixInsuranceReconcile from './SixInsuranceReconcile';

const types = [
  { key: 'work_injury', label: '工伤保险' },
  { key: 'medical', label: '医疗保险' },
  { key: 'unemployment', label: '失业保险' },
  { key: 'maternity', label: '生育保险' },
  { key: 'pension', label: '养老保险' },
  { key: 'commercial', label: '商业险' },
  { key: 'six', label: '六险核对' }, // 新增Tab
];

const rateRender = v => (v === null || v === undefined ? '-' : (Number(v) * 100).toFixed(2) + '%');

export default function ContributionsPage(){
  const [active, setActive] = useState('work_injury');
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterForm] = Form.useForm();
  // 已移除: 批次汇总 & 差异对比相关状态
  // 覆盖差异
  const [coverageDiff, setCoverageDiff] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageCats, setCoverageCats] = useState(['active','intern']);
  // 自动选最新批次并执行差异核对标志
  const [autoDiffInitialized, setAutoDiffInitialized] = useState(false);

  const fetchBatches = async () => {
    setBatchLoading(true);
    try {
      if (active === 'medical') {
        const res = await axios.get(`/medical-insurance-batches/import-batches`);
        setBatchOptions((res.data.data||[]).map(b=>({value:b.import_batch, label:`${b.import_batch} (${b.count})`})));
        // 默认选中最新批次
        if (!selectedBatch && (res.data.data||[]).length) setSelectedBatch(res.data.data[0].import_batch);
      } else {
        const res = await axios.get(`/contributions/${active}/import-batches`);
        setBatchOptions((res.data.data||[]).map(b=>({value:b.import_batch, label:`${b.import_batch} (${b.count})`})));
        if (!selectedBatch && (res.data.data||[]).length) setSelectedBatch(res.data.data[0].import_batch);
      }
    } catch (e){
      if (e?.response?.status === 400 || e?.response?.status === 404) {
        message.error('后端暂不支持该险种批次接口');
      }
    }
    setBatchLoading(false);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = filterForm.getFieldsValue();
      if (selectedBatch) params.import_batch = selectedBatch;
      if (active === 'medical') {
        const res = await axios.get('/medical-insurance-batches', { params });
        setData((res.data.data||[]).filter(r=>!['合计','总计','小计'].includes(String(r.name||'').trim())));
      } else {
        const res = await axios.get(`/contributions/${active}`, { params });
        setData(res.data.data || []);
      }
    } catch(e){
      if (e?.response?.status === 400 || e?.response?.status === 404) {
        message.error('后端未支持当前险种数据接口');
      } else message.error(e?.message || '获取数据失败');
    }
    setLoading(false);
  };

  useEffect(()=>{ fetchBatches(); fetchData(); }, [active, selectedBatch]);
  useEffect(()=>{ if (selectedBatch && !autoDiffInitialized){ fetchCoverageDiff(); setAutoDiffInitialized(true); } }, [selectedBatch, autoDiffInitialized]);
  useEffect(()=>{ setCoverageDiff(null); }, [active, selectedBatch]);

  // 已移除: 批次汇总 / 单险种对比 / 多险种合并对比相关方法

  const handleImport = async (file) => {
    setImportLoading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const url = active === 'medical' ? '/medical-insurance-batches/import' : `/contributions/${active}/import`;
      const res = await axios.post(url, fd, { headers:{'Content-Type':'multipart/form-data'} });
      setImportResult(res.data);
      message.success('导入成功');
      fetchBatches(); fetchData();
    } catch(e){
      if (e?.response?.status === 400 || e?.response?.status === 404) message.error('后端暂未支持此险种导入');
      else message.error(e?.message || '导入失败');
    }
    setImportLoading(false);
  };

  const fetchCoverageDiff = async () => {
    if (!selectedBatch) { message.warning('请选择批次'); return; }
    setCoverageLoading(true); setCoverageDiff(null);
    try {
      const res = await axios.get(`/contributions/${active}/coverage-diff`, { params:{ import_batch: selectedBatch, employeeCategories: coverageCats.join(',') } });
      // 去重 + 排序
      const dedupByName = arr => {
        const map = new Map();
        for (const r of arr||[]) {
          if (!r?.name) continue; // 忽略空姓名
          if (!map.has(r.name)) map.set(r.name, r);
        }
        return Array.from(map.values()).sort((a,b)=> (a.name||'').localeCompare(b.name||''));
      };
      const processed = {
        missingInInsurance: dedupByName(res.data.missingInInsurance),
        extraInInsurance: dedupByName(res.data.extraInInsurance),
        resignedStillCovered: dedupByName(res.data.resignedStillCovered),
      };
      setCoverageDiff(processed);
    } catch(e){ message.error(e?.message || '获取覆盖差异失败'); }
    setCoverageLoading(false);
  };

  // 导出 CSV（全部三类合并 & 单类）
  const exportCsv = (mode='all') => {
    if (!coverageDiff) { message.warning('无差异结果'); return; }
    const rows = [];
    const pushSection = (title, list) => {
      rows.push([title]);
      if (!list.length) { rows.push(['无记录']); return; }
      for (const r of list) rows.push([r.name]);
      rows.push([]); // 空行分隔
    };
    if (mode==='all') {
      pushSection('员工缺少参保', coverageDiff.missingInInsurance);
      pushSection('名单多余人员', coverageDiff.extraInInsurance);
      pushSection('离职仍在保', coverageDiff.resignedStillCovered);
    } else if (mode==='missing') pushSection('员工缺少参保', coverageDiff.missingInInsurance);
    else if (mode==='extra') pushSection('名单多余人员', coverageDiff.extraInInsurance);
    else if (mode==='resigned') pushSection('离职仍在保', coverageDiff.resignedStillCovered);
    const csv = rows.map(r => r.map(v => `"${(v||'').replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `coverage_diff_${active}_${selectedBatch}_${mode}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    message.success('CSV 导出完成');
  };

  const columns = active === 'commercial' ? [
    { title:'姓名', dataIndex:'name', width:120 },
    { title:'证件类型', dataIndex:'certificate_type', width:110 },
    { title:'证件号码', dataIndex:'certificate_number', width:170 },
    { title:'雇主险保费', dataIndex:'employer_insurance_fee', width:120 },
    { title:'安责险保费', dataIndex:'responsibility_insurance_fee', width:120 },
    { title:'合计(应缴)', dataIndex:'amount_due', width:130 },
    { title:'批次', dataIndex:'import_batch', width:140 },
    { title:'导入时间', dataIndex:'imported_at', width:160, render: v => v ? v.replace('T',' ').replace('Z','') : '-' }
  ] : (active === 'medical' ? [
    { title:'姓名', dataIndex:'name', width:120 },
    { title:'证件类型', dataIndex:'certificate_type', width:110 },
    { title:'证件号码', dataIndex:'certificate_number', width:170 },
    { title:'缴费工资', dataIndex:'wage', width:110, render:v=> v==null?'-':v },
    { title:'缴费基数', dataIndex:'base', width:110, render:v=> v==null?'-':v },
    { title:'费率', dataIndex:'rate', width:100, render: v => (v==null||v==='')?'-': (Number(v)*100).toFixed(2)+'%' },
    { title:'应缴费额(元)', dataIndex:'amount_due', width:120, render:v=> v==null?'-':v },
    { title:'批次', dataIndex:'import_batch', width:140 },
    { title:'导入时间', dataIndex:'imported_at', width:160, render: v => v ? v.replace('T',' ').replace('Z','') : '-' }
  ] : [
    { title:'姓名', dataIndex:'name', width:120 },
    { title:'证件类型', dataIndex:'certificate_type', width:100 },
    { title:'证件号码', dataIndex:'certificate_number', width:160 },
    { title:'缴费工资', dataIndex:'wage', width:120 },
    { title:'缴费基数', dataIndex:'base', width:120 },
    { title:'费率', dataIndex:'rate', width:100, render: rateRender },
    { title:'应缴费额(元)', dataIndex:'amount_due', width:140 },
    { title:'批次', dataIndex:'import_batch', width:140 },
    { title:'导入时间', dataIndex:'imported_at', width:160, render: v => v ? v.replace('T',' ').replace('Z','') : '-' }
  ]);

  return (
    <div>
      <h2 style={{marginBottom:16}}>保险管理</h2>
      <Tabs activeKey={active} onChange={k=>{ setActive(k); setSelectedBatch(undefined); setImportResult(null); }} items={types.map(t=>({key:t.key,label:t.label}))} />
      <Space direction="vertical" style={{width:'100%'}} size="large">
        {active === 'six' ? (
          <SixInsuranceReconcile />
        ) : (
          <>
            <div>
              <Upload showUploadList={false} accept=".xls,.xlsx,.csv" customRequest={({file,onSuccess,onError})=>{
                handleImport(file).then(()=>onSuccess('ok')).catch(onError);
              }}>
                <Button icon={<UploadOutlined />} loading={importLoading}>上传{types.find(x=>x.key===active)?.label}缴费表</Button>
              </Upload>
              {importResult && (
                <div style={{marginTop:12}}>
                  <b>导入结果：</b>
                  <pre style={{background:'#f6f6f6', padding:12, maxHeight:240, overflow:'auto'}}>{JSON.stringify(importResult,null,2)}</pre>
                </div>
              )}
            </div>
            <Form form={filterForm} layout="inline" onFinish={fetchData}>
              <Form.Item name="name" label="姓名"><Input placeholder="姓名" allowClear /></Form.Item>
              <Form.Item label="批次">
                <Select allowClear placeholder="选择批次" loading={batchLoading} style={{minWidth:200}} options={batchOptions} value={selectedBatch} onChange={v=>setSelectedBatch(v)} />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button type="primary" onClick={fetchData}>查询</Button>
                  <Button onClick={()=>{ filterForm.resetFields(); setSelectedBatch(undefined); fetchData(); }}>重置</Button>
                  <Button onClick={fetchBatches}>刷新批次</Button>
                  {selectedBatch && (
                    <Button danger onClick={async ()=>{
                      try {
                        const delUrl = active === 'medical' ? `/medical-insurance-batches/import-batches/${selectedBatch}` : `/contributions/${active}/import-batches/${selectedBatch}`;
                        await axios.delete(delUrl);
                        message.success('批次删除成功');
                        setSelectedBatch(undefined);
                        fetchBatches(); fetchData();
                      } catch(e){ message.error(e?.message || '批次删除失败'); }
                    }}>删除当前批次</Button>
                  )}
                </Space>
              </Form.Item>
            </Form>
            {selectedBatch && (
              <div style={{background:'#fafafa', padding:8, border:'1px solid #eee', borderRadius:4}}>
                当前批次：<b>{selectedBatch}</b>；记录数：{data.length}
              </div>
            )}
            {/* 覆盖差异区块（仅显示姓名，已移除汇总/对比模块） */}
            {active !== 'six' && (
              <div style={{ background:'#fff', padding:16, borderRadius:8, border:'1px solid #eee', marginBottom:16 }}>
                <h3 style={{marginBottom:12}}>覆盖差异 (在职+实习 vs {types.find(t=>t.key===active)?.label} 名单)</h3>
                <Space style={{ marginBottom:12 }} wrap>
                  <Select mode="multiple" value={coverageCats} onChange={setCoverageCats} options={[{value:'active',label:'在职'},{value:'intern',label:'实习'},{value:'temp',label:'临时'}]} style={{ minWidth:200 }} placeholder="选择人员类别" />
                  <Button type="primary" onClick={fetchCoverageDiff} loading={coverageLoading} disabled={!selectedBatch}>执行差异核对</Button>
                  {coverageDiff && <Button onClick={()=>setCoverageDiff(null)}>清除结果</Button>}
                  {coverageDiff && (
                    <Space>
                      <Button onClick={()=>exportCsv('all')}>导出全部CSV</Button>
                      <Button onClick={()=>exportCsv('missing')}>导出缺少参保</Button>
                      <Button onClick={()=>exportCsv('extra')}>导出多余人员</Button>
                      <Button onClick={()=>exportCsv('resigned')}>导出离职仍在保</Button>
                    </Space>
                  )}
                </Space>
                {!coverageDiff && <div style={{ fontSize:12, color:'#64748b' }}>选择批次后执行差异核对，显示不在名单 / 名单多余 / 离职仍在保。</div>}
                {coverageDiff && (
                  <div style={{ display:'flex', flexWrap:'wrap', gap:16 }}>
                    <div style={{ flex:'1 1 240px', minWidth:240 }}>
                      <h4 style={{ margin:'0 0 8px', fontSize:13 }}>员工缺少参保 ({coverageDiff.missingInInsurance.length})</h4>
                      <div style={{ maxHeight:220, overflow:'auto', border:'1px solid #e2e8f0', borderRadius:6, padding:6 }}>
                        <ul style={{ listStyle:'none', margin:0, padding:0 }}>
                          {coverageDiff.missingInInsurance.length
                            ? coverageDiff.missingInInsurance.map(r => (
                                <li key={r.name} style={{ padding:'4px 6px', borderBottom:'1px solid #f1f5f9' }}>{r.name}</li>
                              ))
                            : <li style={{ padding:'4px 6px', color:'#94a3b8' }}>无记录</li>}
                        </ul>
                      </div>
                    </div>
                    <div style={{ flex:'1 1 240px', minWidth:240 }}>
                      <h4 style={{ margin:'0 0 8px', fontSize:13 }}>名单多余人员 ({coverageDiff.extraInInsurance.length})</h4>
                      <div style={{ maxHeight:220, overflow:'auto', border:'1px solid #e2e8f0', borderRadius:6, padding:6 }}>
                        <ul style={{ listStyle:'none', margin:0, padding:0 }}>
                          {coverageDiff.extraInInsurance.length
                            ? coverageDiff.extraInInsurance.map(r => (
                                <li key={r.name} style={{ padding:'4px 6px', borderBottom:'1px solid #f1f5f9' }}>{r.name || '-'}</li>
                              ))
                            : <li style={{ padding:'4px 6px', color:'#94a3b8' }}>无记录</li>}
                        </ul>
                      </div>
                    </div>
                    <div style={{ flex:'1 1 240px', minWidth:240 }}>
                      <h4 style={{ margin:'0 0 8px', fontSize:13 }}>离职仍在保 ({coverageDiff.resignedStillCovered.length})</h4>
                      <div style={{ maxHeight:220, overflow:'auto', border:'1px solid #e2e8f0', borderRadius:6, padding:6 }}>
                        <ul style={{ listStyle:'none', margin:0, padding:0 }}>
                          {coverageDiff.resignedStillCovered.length
                            ? coverageDiff.resignedStillCovered.map(r => (
                                <li key={r.name} style={{ padding:'4px 6px', borderBottom:'1px solid #f1f5f9' }}>{r.name}</li>
                              ))
                            : <li style={{ padding:'4px 6px', color:'#94a3b8' }}>无记录</li>}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            <Table rowKey="id" loading={loading} dataSource={data} columns={columns} pagination={{pageSize:20}} />
          </>
        )}
      </Space>
    </div>
  );
}
